import { FC } from 'react';
import { Text, View } from '@react-pdf/renderer';
import { splitSquadTitle } from './pageHeaderTokens';
import { SQUAD_PAGE_HEADER } from './pageHeaderTokens';
import { SquadFondsLogoPdf } from './SquadFondsLogo';

export interface SquadPdfPageHeaderProps {
  /** @deprecated Prefer dynamic counter via react-pdf render prop; kept for call-site compat. */
  page?: number;
  /** Fallback total when `totalPages` is not yet available during layout. */
  total?: number;
  title: string;
  styles: Record<string, any>;
  /** Left meta label, e.g. „Werbemitteilung (Stand: 30.09.2026)“ — see squadPageMetaLabel. */
  metaLabel?: string;
}

const noHyphen = (word: string) => [word];

/**
 * Factsheet PDF header. Page counter uses react-pdf's layout `pageNumber` /
 * `totalPages` so overflow/continuation pages (e.g. long portfolio widgets)
 * get the correct "Seite X von Y", not the first logical section page.
 *
 * Both meta labels must use `render`. In a `fixed` header, react-pdf keeps the
 * dynamic page-number Text and drops a static sibling — which is why the HTML
 * preview showed Werbemitteilung and the PDF did not. wrap/hyphenation are
 * pinned so the 16-char word is not split by the global 14-char wrap callback.
 */
export const SquadPdfPageHeader: FC<SquadPdfPageHeaderProps> = ({
  total = 4,
  title,
  styles,
  metaLabel = 'Werbemitteilung',
}) => {
  const { squad, rest } = splitSquadTitle(title);

  return (
    <View style={styles.pageHeader}>
      <View style={styles.headerSidebarCell}>
        <View style={styles.logoBlock}>
          <SquadFondsLogoPdf
            width={SQUAD_PAGE_HEADER.logoImageWidthPdf}
            height={SQUAD_PAGE_HEADER.logoImageHeightPdf}
          />
        </View>
      </View>
      <View style={styles.headerMainCell}>
        <Text style={styles.mainTitle}>
          {squad ? (
            <>
              <Text style={styles.titleSquad}>{squad}</Text>
              {rest ? <Text style={styles.titleRest}>{` ${rest}`}</Text> : null}
            </>
          ) : (
            <Text style={styles.titleRest}>{title}</Text>
          )}
        </Text>
        <View style={styles.headerMetaRow}>
          <Text
            wrap={false}
            hyphenationCallback={noHyphen}
            style={styles.headerMetaText}
            render={() => metaLabel}
          />
          <Text
            wrap={false}
            hyphenationCallback={noHyphen}
            style={styles.headerMetaText}
            render={({ pageNumber, totalPages }) =>
              `Seite ${pageNumber} von ${totalPages || total}`
            }
          />
        </View>
      </View>
    </View>
  );
};
